import api from 'api/config';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
} from 'store/slices/authSlice';
import { SESSION_KEYS } from 'utils/constant';

const { AUTH_TOKEN } = SESSION_KEYS;

/**
 * Authentication Service for user authentication and session management
 * Provides methods for login, logout, signup, and authentication state management
 *
 * Backend endpoints:
 * - GET /check-auth - Verify authentication status
 * - POST /login - User login
 * - POST /logout - User logout
 * - POST /signup - User registration
 * - POST /update-password - Update user password
 * - GET /user - Get user details
 *
 * Token Management:
 * - Uses sessionStorage for auth tokens (cleared on tab close)
 * - Supports X-Auth-Token header for incognito mode fallback
 * - Automatic token cleanup on logout and auth failures
 */
export const authService = {
  /**
   * Check authentication status and restore session
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise<boolean>} True if authenticated, false otherwise
   */
  checkAuth: async (dispatch) => {
    try {
      dispatch(loginStart());
      const response = await api.get('/check-auth');
      if (response.data?.authenticated && response.data?.user) {
        dispatch(loginSuccess(response.data.user));
        return true;
      }
      // Clear stored tokens if auth check fails
      sessionStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(AUTH_TOKEN);
      dispatch(logout());
      return false;
    } catch (error) {
      // Clear stored tokens on auth error
      sessionStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(AUTH_TOKEN);
      dispatch(logout());
      return false;
    }
  },

  /**
   * User login with credentials
   * @param {string} username - Username
   * @param {string} password - Password
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise<Object>} Login response with user data
   * @returns {Promise<Object>} response.user - Authenticated user details
   */
  login: async (username, password, dispatch) => {
    try {
      dispatch(loginStart());
      const response = await api.post('/login', { username, password });

      // Store token from X-Auth-Token header for incognito mode fallback
      const headerToken = response.headers['x-auth-token'];
      if (headerToken) {
        // Use sessionStorage for better security (cleared on tab close)
        sessionStorage.setItem(AUTH_TOKEN, headerToken);
        // Remove from localStorage if it exists (migration)
        localStorage.removeItem(AUTH_TOKEN);
      }

      dispatch(loginSuccess(response.data.user));
      return response;
    } catch (error) {
      dispatch(
        loginFailure(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message
        )
      );
      throw error;
    }
  },

  /**
   * User logout and session cleanup
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise<Object>} Logout confirmation
   */
  logout: async (dispatch) => {
    try {
      const response = await api.post('/logout');
      // Clear stored tokens on successful logout
      sessionStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(AUTH_TOKEN);
      dispatch(logout());
      return response.data;
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear tokens and dispatch logout even if the API call fails
      sessionStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(AUTH_TOKEN);
      dispatch(logout());
      throw error;
    }
  },

  /**
   * User registration
   * @param {Object} payload - Signup payload (snake_case)
   * @param {string} payload.first_name - First name
   * @param {string} payload.last_name - Last name
   * @param {string} payload.username - Desired username
   * @param {string} payload.password - Password
   * @param {string} payload.email - Email address
   * @param {string} payload.role - Role (e.g. 'user')
   * @param {string} [payload.phone_number] - Optional phone number in E.164 format
   * @returns {Promise<Object>} Registration response
   */
  signup: async (payload) => {
    try {
      const response = await api.post('/signup', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update user password
   * @param {number|string} userId - User ID
   * @param {string} oldPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Password update confirmation
   */
  updatePassword: async (userId, oldPassword, newPassword) => {
    try {
      const response = await api.post('/update-password', {
        user_id: userId,
        old_password: oldPassword,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get user details by ID
   * @param {number|string} userId - User ID to fetch
   * @returns {Promise<Object>} User details
   */
  getUser: async (userId) => {
    try {
      const response = await api.get('/user', { params: { user_id: userId } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Helper method to check if user has a stored token
   * Useful for incognito mode detection
   * @returns {boolean} True if token exists in storage
   */
  hasStoredToken: () => {
    return !!(
      sessionStorage.getItem(AUTH_TOKEN) || localStorage.getItem(AUTH_TOKEN)
    );
  },

  /**
   * Helper method to clear stored tokens
   * Removes tokens from both sessionStorage and localStorage
   */
  clearStoredToken: () => {
    sessionStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(AUTH_TOKEN);
  },

  /**
   * Helper method to get stored token
   * Checks sessionStorage first, then localStorage
   * @returns {string|null} Auth token if found, null otherwise
   */
  getStoredToken: () => {
    return (
      sessionStorage.getItem(AUTH_TOKEN) || localStorage.getItem(AUTH_TOKEN)
    );
  },
};
